import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../core/event-bus';
import { Util } from '../../utils/util';
import { Data } from '../../models/data';
import { Context } from "../../models/context";
import { CouponTypeList } from '../../types/enums';
import { OnlineOrderingService } from "../../services/online-ordering-service";
import { LocalizationService } from "../../services/localization-service";
import { DataLayerService } from '../../services/data-layer-service';
import { DialogCreators } from "../../utils/dialog-creators";
import { ILoyaltyProvider } from "../loyalty-provider/loyalty-provider.interface";
import { IIncompleteDiscount, IOrder, IOrderCoupon } from "../../models/order.interface";
import { IDataCoupon } from "../../models/data.interface";
import { OnlineOrderingUtil } from '../../online-ordering/online-ordering-util';
import { IAddCouponToOrderParams, IRemovedCoupon } from "../../models/coupons.interface";
import { ICouponState, ICouponPostState, ICouponApplyResult } from "./coupon-service.interface";
import { CouponAdapter } from "./coupon-adapter";
import { CouponManager, CouponApplyErrorCode, CartPatch } from 'coupon-ts-lib';
import { User } from '../../models/user';
import { TimedMessage } from '../../components/shared/TimedMessage/timed-message';
import { IDealBuilderCouponDTO } from '../../components/modals/DealBuilder/deal-builder.interface';

export class CouponService {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _user: User;
    private readonly _orderingService: OnlineOrderingService;
    private readonly _localizationService: LocalizationService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _supportedCouponTypes: CouponTypeList[] = [
        CouponTypeList.FIXED_PRICE,
        CouponTypeList.DOLLAR_OFF,
        CouponTypeList.PERCENTAGE_OFF_AN_ITEM,
        CouponTypeList.PERCENTAGE_OFF_AN_ITEM_IN_CATEGORY_OR_BOGO_IN_CATEGORY,
        CouponTypeList.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY,
        CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER,
        CouponTypeList.PERCENTAGE_OFF_ITEM_IN_CATEGORY_BASED_ON_PRICE,
        CouponTypeList.MIX_AND_MATCH_PRICE,
        CouponTypeList.MIX_AND_MATCH_PERCENTAGE,
        CouponTypeList.QUANTITY_PRICE,
        CouponTypeList.QUANTITY_DISCOUNT,
        CouponTypeList.UPGRADE];
    private readonly _errorsWithoutMessage: CouponApplyErrorCode[] = [
        CouponApplyErrorCode.NO_QUALIFYING_ITEMS, CouponApplyErrorCode.CART_TOTAL_IS_ZERO, CouponApplyErrorCode.UNKNOWN, CouponApplyErrorCode.COUPON_TYPE_NOT_SUPPORTED, CouponApplyErrorCode.MINIMUM_QUANTITY_NOT_MET];
    private _couponManager: CouponManager | null;
    private _order: IOrder | null = null;

    constructor(eventBus: EventEmitter, data: Data, context: Context, user: User, orderingService: OnlineOrderingService, localizationService: LocalizationService, dataLayerService: DataLayerService) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._user = user;
        this._orderingService = orderingService;
        this._localizationService = localizationService;
        this._dataLayerService = dataLayerService;
        this._eventBusListeners();
        this._couponManager = null;
    }

    public setOrder(order: IOrder): void {
        this._order = order;
    }

    public getMealDealPrice(coupon: IDataCoupon): number {
        if (!this._couponManager)
            return 0;

        const couponDTO = CouponAdapter.createCouponDTO(coupon);
        const snapshot = CouponAdapter.toEngineCart(this.order);
        return this._couponManager.getPricePreviewForCoupon(couponDTO, snapshot);
    }

    private get order(): IOrder {
        if (!this._order) {
            throw new Error("Order is not initialized in CouponService.");
        }
        return this._order;
    }

    /** Validation Helpers */

    public isStoreSupported(): boolean {
        const storesWithCouponService = this._context.getProperty("useCouponService");
        if (!storesWithCouponService || storesWithCouponService.length == 0) {
            return false;
        }

        if (this._data.storeKey) {
            return storesWithCouponService.includes(this._data.getProperty("StoreInfo").storeId);
        }

        return false;
    }

    public async areAllCouponsSupportedAsync(incompleteDiscounts: IIncompleteDiscount[]): Promise<boolean> {
        //check order coupons
        for (const coupon of this.order.Coupons) {
            if (!coupon.RewardId) {
                const params: ICouponState = {
                    couponId: coupon.CouponId,
                    couponCode: coupon.CouponCode ?? undefined,
                    rewardId: coupon.RewardId ?? undefined,
                    isCampaignCode: coupon.IsCampaignCode,
                    isPromotionCode: coupon.IsPromotionCode,
                    isBankedCurrency: coupon.IsBankedCurrency,
                    sourceId: coupon.SourceId ?? undefined,
                };
                const result = await this.isCouponSupportedAsync(params);
                if (!result) {
                    return false;
                }
            }
        }

        //check incomplete coupons
        for (const incompleteCoupon of incompleteDiscounts) {
            const params: ICouponState = {
                couponId: incompleteCoupon.couponId,
                couponCode: incompleteCoupon.couponCode ?? undefined,
                rewardId: incompleteCoupon.rewardId ?? undefined,
                isCampaignCode: incompleteCoupon.isCampaignCode,
                isPromotionCode: incompleteCoupon.isPromotionCode,
                isBankedCurrency: incompleteCoupon.isBankedCurrency,
                sourceId: incompleteCoupon.sourceId ?? undefined,
            };
            const result = await this.isCouponSupportedAsync(params);
            if (!result) {
                return false;
            }
        }

        //check active coupons
        const autoApplyCoupons: IDataCoupon[] = this._data.getProperty("Coupons").filter(cpn => cpn.IsAutoApply);

        for (const coupon of autoApplyCoupons) {
            if (!this._supportedCouponTypes.includes(coupon.CouponTypeId)) {
                return false;
            }
        }

        return true;
    }

    public async isCouponSupportedAsync(params: ICouponState): Promise<boolean> {
        if (params.couponId) {
            const coupon = this.getCoupon(params.couponId, true, this.order.OrderTypeID, null);
            if (coupon == null) {
                return false;
            }
            return this._supportedCouponTypes.includes(coupon.CouponTypeId);
        }

        if (params.couponCode) {
            const couponTypeId = await this.getCouponTypeIdByCouponCode(params.couponCode);
            if (couponTypeId == null) {
                return false;
            }

            return this._supportedCouponTypes.includes(couponTypeId);
        }

        return this._isSpecialtyCoupon(params.rewardId, params.sourceId === 1, params.isCampaignCode, params.isPromotionCode, params.isBankedCurrency, params.orderId !== undefined)
    }

    // Checks for other coupons if any (like rewards, next order credits, box toppers, promotions, and campaigns as they are not yet supported by the service)
    private _isSpecialtyCoupon(rewardId?: number | null, nextOrderCredit?: boolean, isCampaignCode?: boolean, isPromotionCode?: boolean, isBankedCurrency?: boolean, isBoxTopper?: boolean): boolean {
        return ((rewardId !== undefined && rewardId !== null) || nextOrderCredit === true || isCampaignCode === true || isPromotionCode === true || isBankedCurrency === true || isBoxTopper === true);
    }

    // TODO: Also include this logic into the coupon manager module (US 66666)
    // This is needed on application side as well because other models depend on this validation
    public isCouponAllowedInFundraiser(showError: boolean = true): boolean {
        if (!this.order.Fundraiser) {
            return true; // No fundraiser, so coupon is allowed
        }

        const fundraiser = this._data.getProperty("Fundraisers").find(x => x.FundraiserId == this.order.Fundraiser?.ID);
        if (fundraiser && !fundraiser.IsCouponAllowed) {
            if (showError)
                DialogCreators.messageBoxOk(this._localizationService, this._dataLayerService, this._localizationService.names("REMOVE_FUNDRAISER_FOR_PROMO_CODE"));
            return false;
        }

        return true;
    }

    /** Get Coupon Helpers */

    public async getCouponTypeIdByCouponCode(couponCode: string): Promise<number | null> {
        couponCode = couponCode.toLowerCase();

        const tempOrderDate = this.order.FutureDate ?? this.order.OrderStartTime;
        const orderDate = new Date(new Date(Util.deepClone(tempOrderDate)).setHours(0, 0, 0, 0));
        const coupon = await this._getCouponByCode(couponCode, orderDate, this._data.getProperty("StoreInfo").storeId, this._order?.GUID ?? "");
        if (coupon) {
            return coupon.CouponTypeId;
        }
        return null;
    }

    public getCoupon(couponId: number, checkOTST: boolean, orderTypeID: number | null, channelID: number | null): IDataCoupon | null {
        const coupon = this._data.getProperty("Coupons").find(cpn => cpn.CouponId === couponId);
        if (coupon) {
            coupon.ChannelID = channelID || null;
        }
        if (checkOTST && coupon?.OrderTypeSubTypes && coupon?.OrderTypeSubTypes.length > 0)
            return (coupon.OrderTypeSubTypes.some(x => x.OrderTypeId === orderTypeID) ? coupon : null);

        return coupon || null;
    }

    private async _getCouponByCode(couponCode: string, orderDate: Date, storeId: number, orderGuid: string): Promise<IDataCoupon | null> {
        const data = {
            couponCode: couponCode,
            order_date: orderDate,
            storeId: storeId,
            is_app: Util.isAppView(),
            order_guid: orderGuid
        };

        const response = await this._orderingService.getCouponByCouponCode(data);
        if (response.success && response.couponId != null) {
            const couponId = response.couponId;
            return this._data.getProperty("Coupons").find(x => x.CouponId === couponId) ?? null;
        }

        return null;
    }

    /** Coupon Manager Config */

    private _configureCouponManager(): CouponManager {
        const adapter = new CouponAdapter(this._data, this._user, !!this._context.getProperty("developmentMode"))
        return new CouponManager(adapter);
    }

    private readonly _eventBusListeners = (): void => {
        this._eventBus.on(EventTypes.DATA_RETRIEVED, () => {
            this._couponManager = this._configureCouponManager();
            this._logServiceStatus();
        });
        this._eventBus.on(EventTypes.LOCATION_SELECTION, () => {
            this._logServiceStatus();
        });
        this._eventBus.on(EventTypes.LOGGED_IN, () => {
            this._couponManager = this._configureCouponManager();
            this._logServiceStatus();
        });
        this._eventBus.on(EventTypes.LOGGED_OUT, () => {
            this._couponManager = this._configureCouponManager();
            this._logServiceStatus();
        });
    }

    private _logServiceStatus(): void {
        if (this.isStoreSupported() && this._context.getProperty("developmentMode"))
            console.log(`Store ${this._data.storeKey} has coupon service enabled.`);
        else
            console.log(`Store ${this._data.storeKey} has coupon service disabled.`);
    }

    ///** Apply Logic */

    public async applyCouponByParamsAsync(incompleteDiscounts: IIncompleteDiscount[], state: ICouponState, loyaltyProvider: ILoyaltyProvider, postState?: ICouponPostState): Promise<ICouponApplyResult> {
        postState ??= { showErrorMsg: false, showApplyMsg: false, addIncomplete: true };
        state.runBatch ??= true;

        const coupon = this.getCoupon(state.couponId || 0, true, this.order.OrderTypeID, null);
        if (!coupon)
            return { success: false, hasErrorMsg: false };

        return await this._applyCouponAsync(coupon, incompleteDiscounts, state, loyaltyProvider, postState);
    }

    //TODO: Need to include single use coupons, campaign codes, promotion codes, and special code logic
    public async applyCouponByCodeAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponCode: string): Promise<ICouponApplyResult> {
        if (!this._validateOnApplyCouponCode(loyaltyProvider, couponCode))
            return { success: false, hasErrorMsg: false };

        couponCode = couponCode.toLowerCase();

        const tempOrderDate = this.order.FutureDate ?? this.order.OrderStartTime;
        const orderDate = new Date(new Date(Util.deepClone(tempOrderDate)).setHours(0, 0, 0, 0));
        // Check if normal coupon code, advertisement code, or channel code in that order
        const tmpCpn = await this._getCouponByCode(couponCode, orderDate, this._data.getProperty("StoreInfo").storeId, this._order?.GUID ?? "");
        if (!tmpCpn)
            return { success: false, hasErrorMsg: false };

        const newParams: ICouponState = {
            couponId: tmpCpn.CouponId,
            couponCode: couponCode,
            channelId: tmpCpn.ChannelID ?? undefined,
            isCampaignCode: false,
            isPromotionCode: false,
            isSpecialCode: false
        };
        const result = await this.checkCouponHasQualifyingItemsAsync(incompleteDiscounts, loyaltyProvider, newParams, { showErrorMsg: false, showApplyMsg: true, addIncomplete: true });

        if (this._dataLayerService && couponCode) {
            this._dataLayerService.apply_coupon_code(couponCode, false);
        }
        return result;
    }

    private async _applyCouponAsync(coupon: IDataCoupon, incompleteDiscounts: IIncompleteDiscount[], state: ICouponState, loyaltyProvider: ILoyaltyProvider, postState: ICouponPostState): Promise<ICouponApplyResult> {
        let result = false;
        if (!this._couponManager)
            return { success: false, hasErrorMsg: false };

        const couponDTO = CouponAdapter.createCouponDTO(coupon);
        const snapshot = CouponAdapter.toEngineCart(this.order);
        const couponIdentity = {
            isReward: state.rewardId !== undefined && state.rewardId !== null,
            rewardId: state.rewardId?.toString()
        };

        const applyResult = this._couponManager.apply(couponDTO, snapshot, couponIdentity);
        if (applyResult.success && applyResult.data && applyResult.patch) {
            this._onCouponApplied(applyResult.patch, coupon, postState.showApplyMsg, state);
            result = true;
        } else if (!applyResult.success && applyResult.errorCode) {
            this._onCouponNotApplied(incompleteDiscounts, applyResult.errorCode, coupon, state, postState);
        }

        await this._postApplyLoyaltyProcessingAsync(loyaltyProvider, result, state);
        const errorCode = applyResult.errorCode;
        const hasErrorMsg = errorCode !== undefined && !this._errorsWithoutMessage.includes(errorCode);

        return { success: result, hasErrorMsg: hasErrorMsg };
    }

    private _validateOnApplyCouponCode(loyaltyProvider: ILoyaltyProvider, couponCode: string): boolean {
        if (couponCode === "") {
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayerService, this._localizationService.names("InvalidCouponCode"));
            return false;
        }

        if (loyaltyProvider.onlyOneDiscountAllowed(null))
            return false;

        if (!this.isCouponAllowedInFundraiser())
            return false;

        return true;
    }

    private _onCouponApplied(patch: CartPatch, appliedCoupon: IDataCoupon, showApplyMsg: boolean, state: ICouponState): void {
        const addCouponParams: IAddCouponToOrderParams = {
            couponId: appliedCoupon.CouponId,
            couponCode: state.couponCode ?? '',
            discount: appliedCoupon.Discount,
            discountAmount: patch.discountAmount ?? 0,
            isBeforeTax: appliedCoupon.IsBeforeTax,
            allowOtherCoupons: appliedCoupon.AllowOtherCoupons,
            excludeLoyaltyUsage: appliedCoupon.ExcludeLoyaltyUsage ?? false,
            rewardId: state.rewardId || null,
            channelId: appliedCoupon.ChannelID || null,
            isCampaignCode: state.isCampaignCode || false,
            isPromotionCode: state.isPromotionCode || false,
            isBankedCurrency: state.isBankedCurrency || false,
            isAutoApplying: state.autoApplying || false,
            isActive: true,
            sourceId: null,
            isSpecialCode: state.isSpecialCode || false,
            isSingleUseCoupon: state.isSingleUseCoupon || false
        }
        this._addDiscountsToItems(patch, appliedCoupon.IsPriceReducer);
        const couponKey = this._addCouponToOrder(addCouponParams);
        if (couponKey > 0) {
            this._addCouponToItemsCoupon(patch, couponKey, appliedCoupon.Priority, (appliedCoupon.AllowOtherCoupons ? 0 : 1));
            this._updateMarkedItems(patch);
            this._eventBus.emit(EventTypes.ORDER_COUPON_CHANGED, appliedCoupon);

            if (showApplyMsg) {
                const msg = new TimedMessage(this._localizationService.names("CouponApplied"));
                msg.show();
            }
        }
        Util.setElement("value", "txt_cart_coupon_code", "");
    }

    private _addDiscountsToItems(patch: CartPatch, isPriceReducer: boolean): void {
        for (const item of patch.addDiscounts) {
            const oItem = this.order.Items[item.itemArrayIndex];
            if (isPriceReducer) {
                oItem.Price -= item.beforeTaxDiscount;
                oItem.TaxAmount -= item.beforeTaxDiscount;
                oItem.MenuPrice -= item.beforeTaxDiscount;

                oItem.Price -= item.afterTaxDiscount;
                oItem.MenuPrice -= item.afterTaxDiscount;
            } else {
                oItem.BeforeTaxDiscount += item.beforeTaxDiscount;
                oItem.AfterTaxDiscount += item.afterTaxDiscount;
            }
        }
    }

    private _addCouponToOrder(addCouponToOrderParams: IAddCouponToOrderParams): number {
        const oCoupon: IOrderCoupon = {
            CouponKey: (this.order.Coupons.length === 0) ? 1 : this.order.Coupons[this.order.Coupons.length - 1].CouponKey + 1,
            CouponId: addCouponToOrderParams.couponId,
            CouponCode: addCouponToOrderParams.couponCode,
            Discount: addCouponToOrderParams.discount,
            Amount: addCouponToOrderParams.discountAmount,
            IsBeforeTax: addCouponToOrderParams.isBeforeTax,
            IsActive: addCouponToOrderParams.isActive,
            AllowOtherCoupons: addCouponToOrderParams.allowOtherCoupons,
            ExcludeLoyaltyUsage: addCouponToOrderParams.excludeLoyaltyUsage,
            IsAdded: false,
            IsRemoved: false,
            AutoApplied: addCouponToOrderParams.isAutoApplying || false,
            ChannelID: addCouponToOrderParams.channelId || null,
            IsPromotionCode: addCouponToOrderParams.isPromotionCode || false,
            IsCampaignCode: addCouponToOrderParams.isCampaignCode || false,
            RewardId: addCouponToOrderParams.rewardId || null,
            IsBankedCurrency: addCouponToOrderParams.isBankedCurrency || false,
            RedemptionReference: null,
            BasketItemId: null,
            SourceId: null,
            IsSingleUseCoupon: addCouponToOrderParams.isSingleUseCoupon || false
        };
        this.order.Coupons.push(oCoupon);
        return oCoupon.CouponKey;
    }

    private _addCouponToItemsCoupon(patch: CartPatch, couponKey: number, priority: number, allowOtherCoupons: number): void {
        for (const item of patch.addDiscounts) {
            const itemCoupon = {
                ItemKey: item.itemKey,
                HalfIndex: item.halfIndex,
                ItemRecId: item.itemRecId,
                CouponKey: couponKey,
                QTYIndex: 0, // currently is being used for validating BOGO coupons in original coupon model, omitting its use for now
                BeforeTaxDiscount: item.beforeTaxDiscount,
                AfterTaxDiscount: item.afterTaxDiscount,
                DiscountedQuantity: allowOtherCoupons,
                IsActive: true,
                Priority: priority,
            };
            this.order.ItemsCoupons.push(itemCoupon);
        }
    }

    private _updateMarkedItems(patch: CartPatch): void {
        for (const mItem of patch.markedItems) {
            const oItem = this.order.Items.find(x => x.ItemKey === mItem.itemKey && x.HalfIndex === mItem.halfIndex);
            if (oItem) {
                oItem.DiscountedQuantity = mItem.markedQty;
            }
        }
    }

    private _onCouponNotApplied(incompleteDiscounts: IIncompleteDiscount[], errorCode: CouponApplyErrorCode, nonAppliedCoupon: IDataCoupon, state: ICouponState, postState: ICouponPostState): void {
        if (!this._errorsWithoutMessage.includes(errorCode))
            postState.showErrorMsg = true;
        this._showErrorMessage(nonAppliedCoupon, postState.showErrorMsg, errorCode, state.autoApplying ?? false);

        if ((!postState.showErrorMsg || errorCode == CouponApplyErrorCode.MINIMUM_ORDER_AMOUNT_NOT_MET) && postState.addIncomplete)
            this._addIncompleteCoupon(incompleteDiscounts, nonAppliedCoupon.CouponId, state);
    }

    private _addIncompleteCoupon(incompleteDiscounts: IIncompleteDiscount[], couponId: number, state: ICouponState): void {
        if (incompleteDiscounts.some(cpn => cpn.couponId === couponId))
            return;

        if (!incompleteDiscounts.some(cpn => cpn.couponId === couponId)) {
            incompleteDiscounts.push({
                couponId: couponId,
                couponCode: state.couponCode,
                channelId: null,
                isCampaignCode: state.isCampaignCode,
                isPromotionCode: state.isPromotionCode,
                rewardId: state.rewardId || null
            });
        }
    }

    private _showErrorMessage(coupon: IDataCoupon, showErrorMsg: boolean, errorCode: CouponApplyErrorCode | null, autoApplying: boolean): void {
        if (!showErrorMsg || !errorCode)
            return;

        switch (errorCode) {
            case CouponApplyErrorCode.INVALID_ORDER_TYPE:
                DialogCreators.messageBoxOk(this._localizationService, this._dataLayerService, this._localizationService.names("InvalidOrderTypeCode").replace("??", OnlineOrderingUtil.GetOrderTypeName(this.order.OrderTypeID, this._data.getProperty("OrderTypeSubTypes"), this._localizationService)));
                break;
            case CouponApplyErrorCode.NOT_VALID_TODAY:
                DialogCreators.messageBoxOk(this._localizationService, this._dataLayerService, this._localizationService.names("InvalidScheduleCode"));
                break;
            case CouponApplyErrorCode.COUPON_LIMIT_ONE:
                !autoApplying && DialogCreators.messageBoxOk(this._localizationService, this._dataLayerService, this._localizationService.names("CpnLimitOne"));
                break;
            case CouponApplyErrorCode.COUPON_LIMIT_MET:
                !autoApplying && DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayerService,
                    this._localizationService
                        .names("CPNLIMIT")
                        .replace("??", coupon.LimitsPerOrder.toString()),
                    true
                );
                break;
            case CouponApplyErrorCode.MINIMUM_ORDER_AMOUNT_NOT_MET:
                !autoApplying && DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayerService,
                    this._localizationService
                        .names("CPNMINAMT")
                        .replace("??", coupon.MinimumOrderAmount.toFixed(2)),
                    true
                );
                break;
            case CouponApplyErrorCode.FUNDRAISER_DOES_NOT_ALLOW_COUPON:
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayerService,
                    this._localizationService.names("REMOVE_FUNDRAISER_FOR_PROMO_CODE")
                );
                break;
            case CouponApplyErrorCode.ZERO_DISCOUNT:
                // This behavior is inherited from legacy logic in the POS.
                // The items qualified but the coupon is worth nothing against them.
                !autoApplying && this._showCouponNotQualifiedMessage(coupon);
                break;
            default:
                this._showCouponNotQualifiedMessage(coupon);
        }
    }

    private _showCouponNotQualifiedMessage(coupon: IDataCoupon): void {
        const notQualified = this._localizationService.names("CouponNotQualified");
        const message = coupon.Descriptions
            ? `${notQualified}</br>${this._localizationService.GetDescription(coupon.Descriptions)}`
            : notQualified;

        DialogCreators.messageBoxOk(this._localizationService, this._dataLayerService, message, true);
    }

    private async _postApplyLoyaltyProcessingAsync(loyaltyProvider: ILoyaltyProvider, appliedSuccessfully: boolean, state: ICouponState): Promise<void> {
        if (appliedSuccessfully && state.runBatch) {
            await loyaltyProvider.batchComparison(true);
        }
        if (!appliedSuccessfully) {
            loyaltyProvider.setIncompleteReward(state.rewardId || null);
        }
    }

    //    /** Remove Logic */

    public async removeCouponByKeyAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponKey: number | null, refresh: boolean = false): Promise<void> {
        if (!this._order || !this._couponManager || !couponKey)
            return;

        this._removeDiscountFromItemsAsync(couponKey);
        await this._removeCouponFromOrderAsync(couponKey);

        if (refresh) {
            this._eventBus.emit(EventTypes.ORDER_COUPON_CHANGED, couponKey);
            await this.autoApplyCouponsAsync(incompleteDiscounts, loyaltyProvider);
        }
    }

    public async removeCouponByItemKeyAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, itemKey: number | null, refresh: boolean = false): Promise<IRemovedCoupon[]> {
        if (!this._order || !this._couponManager || !itemKey)
            return [];

        const removedCouponKeys = this._removeCouponFromItemsCoupons(itemKey);
        const removedCoupons = await this._removeCouponFromOrderAndItemsAsync(incompleteDiscounts, loyaltyProvider, removedCouponKeys, refresh);
        return removedCoupons;
    }

    public async removeIncompleteCouponByIdAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponId: number, appliedToOrder = false): Promise<void> {
        const index = incompleteDiscounts.findIndex(x => x.couponId === couponId);
        if (index === -1) return;

        const iDiscount = incompleteDiscounts[index];

        await this.handleLoyaltySideEffects(iDiscount, loyaltyProvider);

        if (!appliedToOrder) {
            this.handleNonAppliedSideEffects(iDiscount);
        }

        incompleteDiscounts.splice(index, 1);

        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    private async handleLoyaltySideEffects(iDiscount: IIncompleteDiscount, loyaltyProvider: ILoyaltyProvider): Promise<void> {
        if (iDiscount.isBankedCurrency) {
            await loyaltyProvider.removeBankedCurrency();
        }

        if (iDiscount.rewardId) {
            const reward = this._user
                .getProperty("LoyaltyData")
                ?.Rewards.find(r => r.RewardId === iDiscount.rewardId);

            if (reward) {
                reward.Used = false;
                this._eventBus.emit(EventTypes.REWARD_REMOVED, reward.RewardId);
            }
        }
    }

    private handleNonAppliedSideEffects(item: IIncompleteDiscount): void {
        const offers = this._user.getProperty("Offers");
        const codeLc = item.couponCode?.toLowerCase();

        if (codeLc && (item.isCampaignCode || item.isPromotionCode)) {
            const offer = offers?.Codes?.find(o => o.CouponCode?.toLowerCase() === codeLc);
            if (offer) offer.Used = false;
        }

        if (item.sourceId === 1) {
            const nextOrderCredit = this._user
                .getProperty("NextOrderCredits")
                ?.find(x =>
                    x.CouponID === item.couponId &&
                    x.CustomerCreditID?.toString() === item.couponCode
                );

            if (nextOrderCredit) nextOrderCredit.Used = false;
        }
    }

    private _removeCouponFromItemsCoupons(itemKey: number | null): number[] {
        const itemsCoupons = this.order.ItemsCoupons;
        const removedCouponKeys: number[] = [];
        for (let i = itemsCoupons.length; i >= 0; i--) {
            const iCoupon = itemsCoupons[i];
            if (!iCoupon)
                continue;

            if (iCoupon.ItemKey === itemKey || itemKey === null) {
                if (!removedCouponKeys.includes(iCoupon.CouponKey)) {
                    removedCouponKeys.push(iCoupon.CouponKey);
                }
                this._removeDiscountFromItem(iCoupon.ItemKey, iCoupon.HalfIndex, iCoupon.BeforeTaxDiscount, iCoupon.AfterTaxDiscount, (iCoupon.DiscountedQuantity === 1));
                itemsCoupons.splice(i, 1);
            }
        }

        return removedCouponKeys;
    }

    private async _removeCouponFromOrderAndItemsAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, removedCouponKeys: number[], refresh: boolean): Promise<IRemovedCoupon[]> {
        const couponsToRemove = this._getRemovedCoupons(removedCouponKeys);
        for (const couponKey of removedCouponKeys) {
            this._removeDiscountFromItemsAsync(couponKey);
            await this._removeCouponFromOrderAsync(couponKey);
            if (refresh) {
                this._eventBus.emit(EventTypes.ORDER_COUPON_CHANGED, couponKey);
                this.autoApplyCouponsAsync(incompleteDiscounts, loyaltyProvider);
            }
        }
        return couponsToRemove;
    }

    private _getRemovedCoupons(removedCouponKeys: number[]): IRemovedCoupon[] {
        const rewards = this._user.getProperty("LoyaltyData")?.Rewards;

        const removedCouponIDs: IRemovedCoupon[] = [];
        for (const couponKey of removedCouponKeys) {
            const coupon = this.order.Coupons.find(x => x.CouponKey === couponKey);
            if (!coupon)
                continue;

            let rewardId: number | null = null;
            const reward = rewards?.find(x => x.RewardId == Number(coupon.RewardId));
            if (reward)
                rewardId = reward.RewardId;

            removedCouponIDs.push({
                couponId: coupon.CouponId,
                offerCode: (coupon.IsCampaignCode || coupon.IsPromotionCode || coupon.IsSpecialCode || coupon.IsSingleUseCoupon)
                    ? coupon.CouponCode : null,
                rewardId: rewardId,
                isBankedCurrency: coupon.IsBankedCurrency,
                isPromotionCode: coupon.IsPromotionCode,
                sourceId: null,
                isCampaignCode: coupon.IsCampaignCode,
                channelId: coupon.ChannelID,
                isSpecialCode: coupon.IsSpecialCode || false,
                isSingleUseCoupon: coupon.IsSingleUseCoupon || false
            });
        }

        return removedCouponIDs;
    }

    private _removeDiscountFromItemsAsync(couponKey: number): void {
        let i = this.order.ItemsCoupons.length;
        while (i--) {
            const iCoupon = this.order.ItemsCoupons[i];
            if (iCoupon.CouponKey === couponKey) {
                this._removeDiscountFromItem(iCoupon.ItemKey, iCoupon.HalfIndex, iCoupon.BeforeTaxDiscount, iCoupon.AfterTaxDiscount, (iCoupon.DiscountedQuantity === 1));
                this.order.ItemsCoupons.splice(i, 1);
            }
        }
    }

    private _removeDiscountFromItem(itemKey: number, halfIndex: number, beforeTaxDiscount: number, afterTaxDiscount: number, clearDiscountedQuantity: boolean): number {
        let arrayIndex = -1;
        const itemsOrder = this.order.Items;
        const itemsData = this._data.getProperty("Items") || []
        for (const [index, oItem] of itemsOrder.entries()) {
            if (oItem.ItemKey === itemKey && oItem.HalfIndex === halfIndex) {
                oItem.BeforeTaxDiscount -= beforeTaxDiscount;
                oItem.AfterTaxDiscount -= afterTaxDiscount;
                if (clearDiscountedQuantity) {
                    oItem.DiscountedQuantity -= 1;
                }
                const mItem = itemsData.find(x => x.ItemId === oItem.ItemId) || null;

                const dependencies = {
                    taxesData: this._data.getProperty("Taxes") || [],
                    zipTaxesData: this._data.getProperty("ZipTaxes") || [],
                    isCallCenter: this._data.getProperty("Settings")?.ISCC || false,
                    orderTypeId: this.order.OrderTypeID,
                    address: this.order.Address
                };
                OnlineOrderingUtil.TaxItem(mItem, oItem, dependencies);
                arrayIndex = index;
            } else if (oItem.ItemKey === itemKey && oItem.HalfCount == 1)
                if (clearDiscountedQuantity && oItem.DiscountedQuantity > 0) {
                    oItem.DiscountedQuantity -= 1;
                }
        }
        return arrayIndex;
    }

    private async _removeCouponFromOrderAsync(couponKey: number): Promise<void> {
        for (let i = 0; i < this.order.Coupons.length; i++) {
            if (this.order.Coupons[i].CouponKey !== couponKey)
                continue;

            const couponCode = this.order.Coupons[i].CouponCode;

            // invalidate if single use coupon
            if (couponCode.toLowerCase().includes("suc_"))
                await this._invalidateSingleUseCouponAsync(couponCode);

            this.order.Coupons.splice(i, 1);
            break;
        }
    }

    private async _invalidateSingleUseCouponAsync(couponCode: string) {
        const data = {
            couponCode: couponCode,
            orderId: this.order.OrderID,
            storeKey: this._data.getProperty("StoreInfo").storeKey
        };

        return await this._orderingService.invalidatePromotionCode(data);
    }

    ///** Auto-Apply Logic */

    public async autoApplyCouponsAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider): Promise<void> {
        await this._autoApplyOrderCouponsAsync(incompleteDiscounts, loyaltyProvider);
        await this._autoApplyIncompleteCouponsAsync(incompleteDiscounts, loyaltyProvider);
        await this._autoApplyActiveCouponsAsync(incompleteDiscounts, loyaltyProvider);
    }

    private async _autoApplyOrderCouponsAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider): Promise<void> {
        for (const coupon of this.order.Coupons) {
            if (!coupon.RewardId) {
                const mCoupon = this.getCoupon(coupon.CouponId, true, this.order.OrderTypeID, coupon.ChannelID);
                const couponTypesList = [CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER, CouponTypeList.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY, CouponTypeList.MIX_AND_MATCH_PRICE, CouponTypeList.MIX_AND_MATCH_PERCENTAGE];
                if (mCoupon && couponTypesList.includes(mCoupon.CouponTypeId)) {
                    await this.removeCouponByKeyAsync(incompleteDiscounts, loyaltyProvider, coupon.CouponKey, true);
                    const params: ICouponState = {
                        couponId: coupon.CouponId,
                        couponCode: coupon.CouponCode
                    };
                    await this.applyCouponByParamsAsync(incompleteDiscounts, params, loyaltyProvider);
                }
            }

            if (coupon.RewardId) {
                const reward = this._user.getProperty("LoyaltyData")?.Rewards.find(x => x.RewardId == coupon.RewardId);
                if (!reward)
                    continue;

                await loyaltyProvider.removeReward(reward);
                await loyaltyProvider.addReward(reward);
            } else if (coupon.IsBankedCurrency) {
                const amount = coupon.Amount;
                await loyaltyProvider.removeBankedCurrency();
                await loyaltyProvider.applyBankedCurrency(amount);
            }
        }
    }

    private async _autoApplyIncompleteCouponsAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider): Promise<void> {
        // Try to Apply Incomplete Coupons
        const tempDiscounts = Util.deepClone(incompleteDiscounts) as IIncompleteDiscount[];
        incompleteDiscounts.length = 0; // Clear incomplete discounts, will re-add if still incomplete after attempt to apply
        for (const coupon of tempDiscounts) {
            if (coupon.rewardId) {
                const reward = this._user.getProperty("LoyaltyData")?.Rewards.find(x => x.RewardId == coupon.rewardId);
                if (reward) {
                    await loyaltyProvider.addReward(reward);
                    break;
                }
            }

            const params: ICouponState = {
                couponId: coupon.couponId,
                couponCode: coupon.couponCode ?? undefined,
                channelId: coupon.channelId ?? undefined,
                autoApplying: true,
                isCampaignCode: coupon.isCampaignCode,
                isPromotionCode: coupon.isPromotionCode,
                sourceId: coupon.sourceId ?? undefined
            };
            await this.applyCouponByParamsAsync(incompleteDiscounts, params, loyaltyProvider);
        }
    }

    private async _autoApplyActiveCouponsAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider): Promise<void> {
        const autoApplyCoupons: IDataCoupon[] = this._data.getProperty("Coupons").filter(cpn => cpn.IsAutoApply);

        for (const coupon of autoApplyCoupons) {
            const params: ICouponState = {
                couponId: coupon.CouponId,
                autoApplying: true,
            };
            await this.applyCouponByParamsAsync(incompleteDiscounts, params, loyaltyProvider, { showErrorMsg: false, showApplyMsg: false, addIncomplete: false });
        }
    }

    ///** Check Qualifying Items Logic */

    public async checkCouponHasQualifyingItemsAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, params: ICouponState, showMessage: ICouponPostState): Promise<ICouponApplyResult> {
        if (!params.couponId)
            return { success: false, hasErrorMsg: false };

        const coupon = this.getCoupon(params.couponId, true, this.order.OrderTypeID, params.channelId ?? null);

        // TODO: Include logic to load coupon from a reward if not found by coupon Id
        //if (!coupon && params.reward) {
        //    await this._loadCouponByIdAsync(params.reward.CouponId);
        //    coupon = this.GetCoupon(params.couponId, true, order.OrderTypeID, params.channelId ?? null);
        //}

        if (!coupon)
            return { success: false, hasErrorMsg: false };

        // TODO: consolidate checks for reward
        //if (loyaltyProvider.onlyOneDiscountAllowed(params.reward?.RewardId ?? null))
        //    return null;

        if (coupon.Items.length > 0) {
            return await this._applyOrOpenMealDealDialogAsync(incompleteDiscounts, loyaltyProvider, params, coupon);
        } else {
            // TODO: When coupon has no qualified items
            // 1. Check if coupon code (also possibly next order credit because the couponId is used in the couponcode field)
            // 2. Check if box topper
            // 3. Check if reward
            // 4. Check if regular coupon Id

            if (params.orderId) {
                params.couponCode = `bx${params.orderId}`;
            }
            if (params.rewardId) {
                //TODO: Add support to add reward
            }

            return await this.applyCouponByParamsAsync(incompleteDiscounts, params, loyaltyProvider, showMessage);
        }
    }

    private async _applyOrOpenMealDealDialogAsync(incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, params: ICouponState, coupon: IDataCoupon): Promise<ICouponApplyResult> {
        if (!params.couponId)
            return { success: false, hasErrorMsg: false };

        const result = await this.applyCouponByParamsAsync(incompleteDiscounts, params, loyaltyProvider, { showErrorMsg: false, showApplyMsg: true, addIncomplete: true });
        if (result.success) {
            await this.removeIncompleteCouponByIdAsync(incompleteDiscounts, loyaltyProvider, params.couponId, true);
        }
        else if (!result.success && !result.hasErrorMsg) {
            const dto: IDealBuilderCouponDTO = {
                coupon: coupon,
                isCampaignCode: params.isCampaignCode ?? false,
                isPromotionCode: params.isPromotionCode ?? false,
                offerCode: params.couponCode ?? null,
                rewardId: params.rewardId ?? null,
                sourceId: params.sourceId ?? null,
                channelId: params.channelId ?? null,
                isSpecialCode: params.isSpecialCode ?? false,
                isSingleUseCoupon: params.isSingleUseCoupon ?? false
            };

            //emit event for opening meal deal dialog
            this._eventBus.emit(EventTypes.MEAL_DEAL_OPEN, dto);
        }

        return result;
    }
}